import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Switch from '@material-ui/core/Switch';
import Select from '@material-ui/core/Select';
import Button from 'react-bootstrap-button-loader';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from "@material-ui/core/TextField";
import InputLabel from '@material-ui/core/InputLabel';
import Dialog from '@material-ui/core/Dialog';
import BeatLoader from 'react-spinners/BeatLoader';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Popup } from "@progress/kendo-react-popup";
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faCircleMinus, faChevronsUp, faChevronsDown, faClock } from "@fortawesome/pro-duotone-svg-icons";
import LoadingLayer from '@controls/LoadingLayer';
import AlertAlgo from '@controls/AlertSnack';
import DecimalTextField from '@controls/DecimalTextField';
import * as storage from '@storage'
import { GetLanguage, IsEnglish } from '@utils/language';
import { format, toLocalDate, convertToDate, isNullOrEmpty, toDecimal, formatDate, parseBool } from '@utils';
import { mode } from '@scripts/Mode';
import { subCodeGroupCode } from '@scripts/SubCodeGroupCode';
import * as actionType from '@scripts/ActionType';
import * as StatusCode from '@scripts/StatusCode';
import { punchSource } from '@scripts/PunchSource';
import * as PageSourceCode from '@scripts/PageSourceCode';
import { punchInsertionType } from '@scripts/PunchInsertionType';
import { clearDBCache } from '@actionsShared/combo';
import { loadForm, refreshFormSrv, savePunch, deletePunch } from '@actionsSup/punchApproval';
import i18next from 'i18next';
import { withNamespaces } from 'react-i18next';
import moment from "moment";
import "moment/locale/fr";
import { loadLocalization } from '@utils/kendoLocalization';
import styles from '@styles/punchApproval.module.css';
import { makeStyles } from '@material-ui/core/styles';

loadLocalization();
moment.locale(GetLanguage());

const useStyles = makeStyles((theme) => ({
    divInput: {
        display: 'table',
        marginBottom: '10px',
        width: '100%',
        '& > div.MuiInputBase-root, div.MuiFormControl-root': {
            width: '100%'
        }
    },
    divInput_80: {
        display: 'table',
        marginBottom: '0px',
        width: '100%',
        '& > label.MuiFormLabel-root': {
            marginBottom: '0px !important'
        },
        '& > div.MuiInputBase-root, div.MuiFormControl-root': {
            width: '90%'
        },
        '& > div.k-datetimepicker': {
            width: '50%'
        },
    },
    divIconRemove: {
        float: 'right',
    },
    inputDuration: {
        '& > label': {
            /*top: '-23.5px'*/
        },
        '& > label.MuiFormLabel-filled': {
            top: '0px'
        },
        '& > label.Mui-focused': {
            top: '0px'
        }
    },
    select: {
        width: '100%',
        '& .MuiSelect-select': {
            width: '100%',
        }
    },
    select90: {
        width: '100%',
        '& .MuiSelect-select': {
            width: 'calc(100% - 10px)',
        }
    },
    iconAddPayCode: {
        color: '#5c7fb9',
        fontSize: '25px',
        float: 'right',
    },
    iconRemovePayCode: {
        color: '#f30808',
        fontSize: '25px',
        float: 'right',
        marginTop: '10px'
    },
    iconClock: {
        color: '#5c7fb9',
        fontSize: '25px',
        float: 'right',
        marginTop: '3px'
    },
    faDisabled: {
        color: '#5c7fb9',
        fontSize: '25px',
        float: 'right',
        marginTop: '3px',
        opacity: '0.6',
        cursor: 'not-allowed',
        pointerEvents: 'none'
    },
    iconClock2: {
        color: '#5c7fb9',
        fontSize: '25px',
        float: 'right',
        marginTop: '20px'
    },
    faDisabled2: {
        color: '#5c7fb9',
        fontSize: '25px',
        float: 'right',
        marginTop: '10px',
        opacity: '0.6',
        cursor: 'not-allowed',
        pointerEvents: 'none'
    },
    customRoot: {
        display: 'unset !important',
        paddingBottom:'0px !important'
    }
}));

const maxPayCode = 6;

const pKeys = Object.freeze({
    IS_APPROVED: 'isApproved',
    IS_OVERTIMEBEFORE: 'isOvertimeBefore',
    IS_OVERTIMEAFTER: 'isOvertimeAfter',
    IS_RECALCULATE_TIME: 'isRecalculateTime',
    TASK: 'taskCode',
    PAY_ACTIVITY: 'payActivityCode',
    PAY_PROJECT: 'payProjectCode',
    ABC_ACTIVITY: 'abcActivityCode',
    ABC_PROJECT: 'abcProjectCode'
});

const config = Object.freeze({
    isProjectEntry: storage.getIsProjectEntry(),
    isActivityEntry: storage.getIsActivityEntry(),
    isTaskEntry: storage.getIsTaskEntry(),
    isUseAbc: storage.getIsUseAbc(),
    isUseActivityRemark: storage.getIsUseActivityRemark(),
    isBlockAssocWorkorderInPunch: storage.getIsBlockAssocWorkorderInPunch(),
    isUseOtherClassification: storage.getUseOtherClassification(),
    isUseWorkorderInPunch: storage.getIsUseWorkorderInPunch(),
    isUseRequisitionCodeOnPunch: storage.getUseRequisitionCodeOnPunch(),
    isUseOrderFormReq: storage.getIsUseOrderFormReq(),
    usePersonnelRequisitionMileage: storage.getUsePersonnelRequisitionMileage(),
    commentForApprovOnPunch: storage.getCommentForApprovOnPunch(),
    isJobTitleEntry: storage.getIsJobTitleEntry(),
    isPayProjectLoadedFromWorkorder: storage.getIsPayProjectLoadedFromWorkorder(), // isWorkOrderLinkedToDepartment && isPayProjectLoadedFromWorkorder
    isWorkOrderLinkedToDepartment: storage.getIsWorkOrderLinkedToDepartment(), // isWorkOrderLinkedToDepartment && isPayProjectLoadedFromWorkorder
    allowConsultation: storage.getAllowConsultation(),
    allowApprobation: storage.getAllowApprobation(),
    isPayProjectWithSkills: storage.getIsPayProjectWithSkills(),
    isActivityAssocWorkorderInPunch: storage.getIsActivityAssocWorkorderInPunch(),
    isDisplayJobTitleProfileByDepartment: storage.getIsDisplayJobTitleProfileByDepartment(),
    isDisplayPayProjectProfileByDepartment: storage.getIsDisplayPayProjectProfileByDepartment(),
    isDisplayShiftProfileByProject: storage.getIsDisplayShiftProfileByProject(),
    isUseBankExtraTime: storage.getIsUseBankExtraTime(),
    isUseExtraTimePay: storage.getIsUseExtraTimePay(),
    isDisplaySubcodeProfileByDepartment: storage.getIsDisplaySubcodeProfileByDepartment(),
    isDisplaySubcodeProfileByJobTitle: storage.getIsDisplaySubcodeProfileByJobTitle(),
    isDisplaySubcodeProfileByProject: storage.getIsDisplaySubcodeProfileByProject(),
    hasEmploymentStatusOnShift: storage.getHasEmploymentStatusOnShift(),
    defaultSubcodTransacWithExtraTimeBank: storage.getDefaultSubcodTransacWithExtraTimeBank(),
    defaultSubcodTransacWithExtraTimePay: storage.getDefaultSubcodTransacWithExtraTimePay(),
    defaultABCProjectHoliday: storage.getDefaultABCProjectHoliday(),
    defaultABCActivityHoliday: storage.getDefaultABCActivityHoliday(),
    defaultPayProjectHoliday: storage.getDefaultPayProjectHoliday(),
    defaultPayActivityHoliday: storage.getDefaultPayActivityHoliday(),
    defaultPayTaskHoliday: storage.getDefaultPayTaskHoliday(),
    defaultABCProjectHolidayCrypt: storage.getDefaultABCProjectHolidayCrypt(),
    defaultABCActivityHolidayCrypt: storage.getDefaultABCActivityHolidayCrypt(),
    isDepartmentMandatoryOnProject: storage.getIsDepartmentMandatoryOnProject(),
    defaultWorkOrder: storage.getDefaultWorkOrder(),
    isLinkProjectActivityTaskP: storage.getIsLinkProjectActivityTaskP(),
    isProjAssocWorkorderInPunch: storage.getIsProjAssocWorkorderInPunch(),
    isDefaultApprovManEntryApprovalPortal: storage.getIsDefaultApprovManEntryApprovalPortal(),
    isUseWorkorder: storage.getIsUseWorkorder(),
    doManageHolidayPaycode: !isNullOrEmpty(storage.getDefaultABCProjectHoliday())
        || !isNullOrEmpty(storage.getDefaultABCActivityHoliday())
        || !isNullOrEmpty(storage.getDefaultPayProjectHoliday())
        || !isNullOrEmpty(storage.getDefaultPayActivityHoliday())
        || !isNullOrEmpty(storage.getDefaultPayTaskHoliday())
});

const cache = {
    isOpenDatePickerView: false,
    fullName: null,
    distributionType: null,
    currentEmploymentStatus: null,
    isPool: false,
    adjustmentPunch: false,
    isPunchRestricted: false,
    isFormCanBeEnabled: true,
    hasExpAccNotApprovedBool: false,
    isHolidayPaycodeDefaults: false,
    abcProjectCode: {
        isCached: false,
        value: null,
        enable: true
    },
    abcActivityCode: {
        isCached: false,
        value: null,
        enable: true,
    },
    payProjectCode: {
        isCached: false,
        value: null,
        enable: true,
    },
    payActivityCode: {
        isCached: false,
        value: null,
        enable: true,
    },
    taskCode: {
        isCached: false,
        value: null,
        enable: true,
    },
};

const PayCodeControls = (props) => {
    const classes = useStyles();
    let isRequired = false;
    if (props.dataItem.index === 1
        || (!isNullOrEmpty(props.dataItem.payCode) || (!isNullOrEmpty(props.dataItem.duration) && props.dataItem.duration > 0))) {
        isRequired = true;
    }

    return (
        <div className={`${styles.divRootPayCode}`}>            
            <div className={styles.divPayCode}>
                <FormControl fullWidth className={`${classes.customRoot}`}>
                    <InputLabel className={isRequired && 'required'} id={`label-payCode${props.dataItem.index}`}>{`${i18next.t('punchApproval:form.payCode')} ${props.dataItem.index}`}</InputLabel>
                    <Select
                        labelId={`label-payCode${props.dataItem.index}`}
                        id={`payCode${props.dataItem.index}`}
                        name={`payCode${props.dataItem.index}`}
                        disabled={props.disableAttrbModel[`payCode${props.dataItem.index}`]}
                        value={props.dataItem.payCode || ''}
                        onChange={(e) => props.handleChange(e, props.dataItem.index, 'payCode')}                        
                        className={classes.select90}
                        MenuProps={{ classes: { paper: styles.selectList } }}
                    >
                        {props.payCodes && props.payCodes.map((obj, index) =>
                            <MenuItem key={index} className={styles.selectItem} value={obj.sc}>{`${obj.sc} - ${IsEnglish() ? obj.scE : obj.scF}`}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </div>
            <div className={styles.divDuration}>
                <FormControl fullWidth className={`${classes.customRoot}`}>
                    <DecimalTextField
                        id={`duration${props.dataItem.index}`}
                        name={`duration${props.dataItem.index}`}
                        label={`${i18next.t('punchApproval:form.duration')} ${props.dataItem.index}`}
                        handleChange={(e) => props.handleChange(e, props.dataItem.index, 'duration')}
                        className={classes.inputDuration + (isRequired ? ' required' : '')}
                        disabled={props.disableAttrbModel[`duration${props.dataItem.index}`]}
                        value={props.dataItem.duration}
                        onFocus={props.handleFocus}
                        decimalScale={4}
                    >
                    </DecimalTextField>
                </FormControl>
            </div>
            {props.items.length > 1 && props.dataItem.index > 1 &&
                <div className={classes.divIconRemove}>
                    <FontAwesomeIcon onClick={() => props.handleRemove(props.dataItem.index)} icon={faCircleMinus} className={props.disableAttrbModel.removePayCodeBtn ? classes.faDisabled : classes.iconRemovePayCode} />
                </div>
            }
        </div>
    )
}

function PunchForm(props) {

    let _el, _el2;
    const classes = useStyles();
    const alertRef = useRef(null);

    const [locale, setLocale] = React.useState(GetLanguage());    
    const [isProcessing, setIsProcessing] = React.useState(true);
    const [openConfirm, setOpenConfirm] = React.useState(false);

    const [showAdditionalInfo, setShowAdditionalInfo] = React.useState(false);
    const [showElectronicDates, setShowElectronicDates] = React.useState(false);
    const [isDeleteBtnVisible, setIsDeleteBtnVisible] = React.useState(false);
    const [showSupCommentRequiredStar, setShowSupCommentRequiredStar] = React.useState(true);
    const [showEndDateRequiredStar, setShowEndDateRequiredStar] = React.useState(true);
    const [showJobTitleRequiredStar, setShowJobTitleRequiredStar] = React.useState(true);
    const [showEmployeeRequiredStar, setShowEmployeeRequiredStar] = React.useState(true);

    const [typeMode, setTypeMode] = React.useState(props.mode);
    const [messageConfirm, setMessageConfirm] = React.useState(null);
    const [dataPayCodesForm, setDataPayCodesForm] = React.useState([]);

    const [employees, setEmployees] = React.useState(null);
    const [payCodesReg, setPayCodesReg] = React.useState(null);
    const [payCodesOthers, setPayCodesOthers] = React.useState(null);
    const [shifts, setShifts] = React.useState([]);
    const [departments, setDepartments] = React.useState([]);
    const [jobTitles, setJobTitles] = React.useState([]);
    const [abcProjects, setABCProjects] = React.useState([]);
    const [abcActivities, setABCActivities] = React.useState([]);
    const [payProjects, setPayProjects] = React.useState([]);
    const [payActivities, setPayActivities] = React.useState([]);
    const [tasksPay, setTasksPay] = React.useState([]);
    const [workorders, setWorkorders] = React.useState([]);
    const [otherClassifs, setOtherClassifs] = React.useState([]);
    const [requisitions, setRequisitions] = React.useState([]);    
    
    const [model, setModel] = React.useState(
        {
            punchId: 0,
            punchEnCrypt: props.id,
            approverId: null,
            approverCrypt: null,
            employeeCode: null,
            epcdCrypt: null,
            distributionType: null,
            origin: null,
            workOrderId: null,
            insertionType: null,
            isLimitedEdition: false,
            isOvertimeBefore: false,
            isOvertimeAfter: false,
            isRecalculateTime: false,
            realStartTime: new Date(),
            realEndTime: new Date(),
            punchStart: null,
            punchEnd: null,
            comments: null,
            departmentCode: null,
            jobTitle: null,
            payrollComment: null,
            employeComment: null,
            payProjectCode: null,
            payActivityCode: null,
            abcProjectCode: null,
            abcActivityCode: null,
            workOrder: null,
            taskCode: null,
            activityNote: null,
            otherClassification: null,
            requisitionCode: null,
            purchaseOrder: null,
            paidMileage: null,
            chargedMileage: null,
            payCode1: null,
            payCode2: null,
            payCode3: null,
            payCode4: null,
            payCode5: null,
            payCode6: null,
            duration1: null,
            duration2: null,
            duration3: null,
            duration4: null,
            duration5: null,
            duration6: null,
            bloc: null,
            isApproved: config.allowApprobation && config.isDefaultApprovManEntryApprovalPortal,
            workRangeId: 0
        }
    );

    const [disableAttrbModel, setDisableAttrbModel] = React.useState(
        {
            employeeCode: typeMode !== mode.ADD,
            isOvertimeBefore: false,
            isOvertimeAfter: false,
            isRecalculateTime: false,
            realStartTime: false,
            realEndTime: false,
            punchStart: true,
            punchEnd: true,
            comments: false,
            departmentCode: false,
            jobTitle: false,
            payrollComment: false,
            employeComment: true,
            payProjectCode: false,
            payActivityCode: false,
            abcProjectCode: false,
            abcActivityCode: false,
            workOrder: false,
            taskCode: false,
            activityNote: false,
            otherClassification: false,
            requisitionCode: false,
            purchaseOrder: false,
            paidMileage: false,
            chargedMileage: false,
            payCode1: false,
            payCode2: false,
            payCode3: false,
            payCode4: false,
            payCode5: false,
            payCode6: false,
            duration1: false,
            duration2: false,
            duration3: false,
            duration4: false,
            duration5: false,
            duration6: false,
            bloc: true,
            isApproved: false,
            workRangeId: false,
            saveButton: false,
            applyButton: false,
            deleteButton: false,
            assignButton: false,
            assignStartButton: false,
            assignEndButton: false,
            removePayCodeBtn: false,
            addPayCodeBtn:false
        }
    );
    
    useLayoutEffect(() => {
        resizeForm();
    }, []);

    useEffect(() => {
        const onLoad = async () => {
            clearCache();
            const response = await clearDBCache(PageSourceCode.PAGE_PUNCH_EDIT);
            if (response?.status !== StatusCode.STATUS_SUCCESS) {
                alertRef.current.showMessageError(i18next.t('error:generic-error'));
            } else {
                props.setIsReload(false);
                onLoadForm();
            }
        };
        
        onLoad();

        // Cleanup on unmount
        return () => {
            clearCache();
            clearDBCache(PageSourceCode.PAGE_PUNCH_EDIT);
        };
    }, []);

    /**
     * Functions def.
     **/
    const resizeForm = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const titleBar = document.querySelector('#appBarTitle').clientHeight;
            const menu = document.querySelector('#menuFavorite').clientHeight;
            const offSet = 105;
            const customHeight = height - titleBar - menu - offSet;
            const element = document.querySelector('#divPunchForm');
            if (element !== undefined && element !== null) {
                element.style.height = `${customHeight}px`;
                element.style.overflowX = 'hidden';
            }
        }, 350);
    };

    const onLoadForm = async (punchCrypt = null) => {

        const pCrypt = punchCrypt ?? model.punchEnCrypt;
        const currentMode = !punchCrypt ? props.mode : mode.EDIT;        

        // Clear caches && reset models
        if (punchCrypt) {
            resetForm();
            const response = await clearDBCache(PageSourceCode.PAGE_PUNCH_EDIT);
            if (response?.status !== StatusCode.STATUS_SUCCESS) {
                onClose(true, false, true);
                return;
            }
        }
        
        let newDisableAttrbModel = disableForm(false);

        const response = await loadForm(PageSourceCode.PAGE_PUNCH_EDIT, currentMode, pCrypt, props.epct);

        if (response?.status === StatusCode.STATUS_SUCCESS) {

            const newModel = loadModel(response);

            // Refresh form
            refreshComboProperties(newModel, response);           

            setShowAdditionalInfo(currentMode === mode.ADD);
            setShowElectronicDates(currentMode !== mode.ADD);

            // Manage restrictions
            if (response.loadError) {
                alertRef.current.showMessageError(i18next.t('punchApproval:message.errorMessageFormatPunchNotFound'));
                return;
            }

            if (punchCrypt) {
                props.setTitlePunchForm(format(i18next.t('punchApproval:title.edit_punch'), newModel.punchId));
            }            

            // Init cache
            cache.fullName = response.model.fullName;            
            cache.isPunchRestricted = response.isPunchRestricted;
            cache.distributionType = newModel.distributionType;
            cache.adjustmentPunch = newModel.origin == punchSource.ADJUSTMENTPUNCH;            

            if (response.comboModel.isChangeEmploymentStatus) {
                cache.currentEmploymentStatus = response.comboModel.currentEmploymentStatus;
            }

            refreshPoolCache(newModel.departmentCode, response.comboModel.departments);

            // Init paycodes
            let allItems = [];
            if (currentMode !== mode.ADD) {
                let maxIndex = 1;
                for (let i = 1; i < maxPayCode + 1; i++) {
                    if (!isNullOrEmpty(newModel[`payCode${i}`])
                        || (!isNullOrEmpty(newModel[`duration${i}`]) && newModel[`duration${i}`] != 0)) {
                        maxIndex = i;
                    }
                }

                for (let i = 1; i < maxIndex + 1; i++) {
                    allItems.push({
                        index: i,
                        payCode: newModel[`payCode${i}`],
                        duration: newModel[`duration${i}`],
                    });
                }
            }
            
            if (allItems.length > 0) {
                setDataPayCodesForm(allItems);
            } else {
                addItem();
            }

            if (cache.hasExpAccNotApprovedBool) {
                alertRef.current.showMessageError(i18next.t('punchApproval:message.errorDisapprouvedExpenseAccountPunch'));
            }

            if (!cache.isFormCanBeEnabled || cache.adjustmentPunch) {
                newDisableAttrbModel = disableForm();
                return;
            } else {
                newDisableAttrbModel = disableForm(false, cache.isPunchRestricted);

                // Set enabled setting
                if (typeMode !== mode.ADD) {

                    newDisableAttrbModel.employeeCode = true;

                    if (cache.isPunchRestricted) {
                        newDisableAttrbModel.saveButton = true;
                        newDisableAttrbModel.applyButton = true;
                        newDisableAttrbModel.assignButton = true;
                        newDisableAttrbModel.assignStartButton = true;
                        newDisableAttrbModel.assignEndButton = true;
                        newDisableAttrbModel.removePayCodeBtn = true;
                        newDisableAttrbModel.addPayCodeBtn = true;
                    }

                    if (cache.distributionType === 'R') {
                        newDisableAttrbModel.realStartTime = true;
                        newDisableAttrbModel.realEndTime = true;
                        newDisableAttrbModel.isApproved = true;
                        newDisableAttrbModel.isOvertimeBefore = true;
                        newDisableAttrbModel.isOvertimeAfter = true;
                        newDisableAttrbModel.employeeCode = true;
                        newDisableAttrbModel.workRangeId = true;
                        newDisableAttrbModel.workOrder = true;
                        newDisableAttrbModel.payActivityCode = true;
                        newDisableAttrbModel.abcProjectCode = true;
                        newDisableAttrbModel.abcActivityCode = true;
                        newDisableAttrbModel.activityNote = true;
                        newDisableAttrbModel.assignButton = true;
                        newDisableAttrbModel.assignStartButton = true;
                        newDisableAttrbModel.assignEndButton = true;
                    }

                    if (config.allowApprobation && !newDisableAttrbModel.isApproved) {
                        newDisableAttrbModel.isApproved = cache.isPool;
                    }
                } else if (typeMode === mode.ADD && !isNullOrEmpty(props.epct)) {
                    if (response.empInfos) {
                        checkDepartementInPool(newModel, newDisableAttrbModel);
                    }
                }

                if (!config.allowApprobation) {
                    newDisableAttrbModel.isApproved = true;
                }
            }            

            if (currentMode !== mode.ADD) {

                if (config.isUseAbc) {
                    if (cache.isFormCanBeEnabled) {
                        newDisableAttrbModel.abcProjectCode = cache.adjustmentPunch;
                        newDisableAttrbModel.abcActivityCode = cache.adjustmentPunch;
                    }
                }

                if (cache.isFormCanBeEnabled) {
                    const active = isNullOrEmpty(newModel.workOrder) && !cache.adjustmentPunch;
                    if (!isNullOrEmpty(newModel.payProjectCode)) {
                        newDisableAttrbModel.payProjectCode = !active;
                    }
                    newDisableAttrbModel.abcProjectCode = !active;
                    newDisableAttrbModel.abcActivityCode = !active;
                }

                if (cache.adjustmentPunch) {
                    const minDuration = -168; // Duration 1 to 6 (7 day * 24 hours = 168 hours)
                    newModel.duration1 = minDuration;
                    newModel.duration2 = minDuration;
                    newModel.duration3 = minDuration;
                    newModel.duration4 = minDuration;
                    newModel.duration5 = minDuration;
                    newModel.duration6 = minDuration;
                }

                if (cache.hasExpAccNotApprovedBool) {
                    newModel.isApproved = false;
                } else if (cache.isPool) {
                    newModel.isApproved = !isNullOrEmpty(newModel.approverCrypt);
                } else if (!isNullOrEmpty(newModel.approverCrypt)) {
                    newModel.isApproved = true;
                } else {
                    newModel.isApproved = config.allowApprobation && config.isDefaultApprovManEntryApprovalPortal
                }

                // manageHolidayPaycode
                const result = await refreshFormSrv(PageSourceCode.PAGE_PUNCH_EDIT,
                    currentMode,
                    actionType.ONLOAD.index,
                    {
                        punchEnCrypt: pCrypt,
                        abcProjectCode: newModel.abcProjectCode,
                        abcActivityCode: newModel.abcActivityCode,
                        payProjectCode: newModel.payProjectCode,
                        payActivityCode: newModel.payActivityCode,
                        taskCode: newModel.taskCode,
                        requisitionCode: newModel.requisitionCode,
                        payCode1: newModel.payCode1,
                        payCode2: newModel.payCode2,
                        payCode3: newModel.payCode3,
                        payCode4: newModel.payCode4,
                        payCode5: newModel.payCode5,
                        payCode6: newModel.payCode6
                    },
                    0,
                    cache.isHolidayPaycodeDefaults,
                    cache.currentEmploymentStatus);

                if (result?.status === StatusCode.STATUS_SUCCESS) {
                    manageHolidayPaycode(newModel, newDisableAttrbModel, {
                        isHolidayPaycodeDefaults: result.comboModel.isHolidayPaycodeDefaults,
                        isChangeHolidayPaycodeDefaults: result.comboModel.isChangeHolidayPaycodeDefaults,
                        payProjectsList: result.comboModel.isReloadPayProjectList ? result.comboModel.payProjects : response.comboModel.payProjects,
                        payActivitiesList: result.comboModel.isReloadPayActivityList ? result.comboModel.payActivities : response.comboModel.payActivities,
                        tasksPayList: result.comboModel.isReloadTaskList ? result.comboModel.tasksPay : response.comboModel.tasksPay,
                    });

                    // Refresh cache
                    if (result.comboModel.isChangeHolidayPaycodeDefaults) {
                        cache.isHolidayPaycodeDefaults = result.comboModel.isHolidayPaycodeDefaults;
                    }

                    // Refresh form
                    refreshComboProperties(newModel, result);

                } else {
                    alertRef.current.showMessageError(i18next.t('error:generic-error'));
                    onClose(false);
                }
                
            } else if (!isNullOrEmpty(newModel.workOrder)) {
                fillProjectComboBoxByWorkorder(newModel, newDisableAttrbModel);
            }

            // Manage required star / Vsisbility
            setShowEndDateRequiredStar(newModel.isApproved || (newModel.punchEnd && newModel.insertionType == punchInsertionType.Punch));
            setShowJobTitleRequiredStar(config.isJobTitleEntry);
            setShowSupCommentRequiredStar(config.commentForApprovOnPunch === 'S' || config.commentForApprovOnPunch === 'D');
            setShowEmployeeRequiredStar(cache.isFormCanBeEnabled);

            if (!config.allowConsultation &&
                (!newModel.isLimitedEdition || !cache.isPunchRestricted) &&
                (cache.adjustmentPunch || (newModel.insertionType === punchInsertionType.MANUAL && isNullOrEmpty(newModel.approverId) && isNullOrEmpty(response.model.approverIdLv1)))) {
                setIsDeleteBtnVisible(true);
            }            

            checkDepartementInPool(newModel, newDisableAttrbModel);

            if (response.isReadOnly && currentMode === mode.EDIT) {
                setShowEmployeeRequiredStar(false);
                cache.isFormCanBeEnabled = false;
                newDisableAttrbModel = disableForm();
            }
            
            setModel(newModel);
            setDisableAttrbModel(newDisableAttrbModel);
            setIsProcessing(false);
        } else {
            alertRef.current.showMessageError(i18next.t('error:generic-error'));
            onClose(false);
        }
    };

    const loadModel = (response) => {
        return {
            punchId: response.model.punchId,
            punchEnCrypt: response.model.punchEnCrypt,
            approverId: response.model.approverId,
            approverCrypt: response.model.approverCrypt,
            employeeCode: response.model.employeeCode,
            epcdCrypt: response.model.epcdCrypt,
            distributionType: response.model.distributionType,
            origin: response.model.origin,
            workOrderId: response.model.workOrderId,
            insertionType: response.model.insertionType,
            isLimitedEdition: response.model.isLimitedEdition,
            isOvertimeBefore: response.model.isOvertimeBefore,
            isOvertimeAfter: response.model.isOvertimeAfter,
            isRecalculateTime: response.model.isRecalculateTime,
            realStartTime: toLocalDate(response.model.startDate),
            realEndTime: toLocalDate(response.model.endDate),
            punchStart: toLocalDate(response.model.punchStart),
            punchEnd: toLocalDate(response.model.punchEnd),
            comments: response.model.comments,
            departmentCode: response.model.departmentCode,
            jobTitle: response.model.jobTitle,
            payrollComment: response.model.payrollComment,
            employeComment: response.model.employeComment,
            payProjectCode: response.model.payProjectCode,
            payActivityCode: response.model.payActivityCode,
            abcProjectCode: response.model.abcProjectCode,
            abcActivityCode: response.model.abcActivityCode,
            workOrder: response.model.workOrder,
            taskCode: response.model.taskCode,
            activityNote: response.model.activityNote,
            otherClassification: response.model.otherClassification,
            requisitionCode: response.model.requisitionCode,
            purchaseOrder: response.model.purchaseOrder,
            paidMileage: response.model.paidMileage,
            chargedMileage: response.model.chargedMileage,
            payCode1: response.model.payCode1,
            payCode2: response.model.payCode2,
            payCode3: response.model.payCode3,
            payCode4: response.model.payCode4,
            payCode5: response.model.payCode5,
            payCode6: response.model.payCode6,
            duration1: response.model.duration1,
            duration2: response.model.duration2,
            duration3: response.model.duration3,
            duration4: response.model.duration4,
            duration5: response.model.duration5,
            duration6: response.model.duration6,
            bloc: response.model.bloc,
            isApproved: response.model.isApproved,
            workRangeId: response.model.workRangeId
        };
    };

    const disableForm = (disable = true, isRestricted = false) => {

        const punchStartDs = disableAttrbModel.punchStart;
        const punchEndDs = disableAttrbModel.punchEnd;
        const employeCommentDs = disableAttrbModel.employeComment;
        const blocDs = disableAttrbModel.bloc;
        const deleteButtonDs = disableAttrbModel.deleteButton;

        return {
            employeeCode: disable,
            departmentCode: disable,
            realStartTime: disable,
            realEndTime: disable,
            jobTitle: disable,
            isOvertimeBefore: disable,
            isOvertimeAfter: disable,
            duration1: disable,
            duration2: disable,
            duration3: disable,
            duration4: disable,
            duration5: disable,
            duration6: disable,
            isApproved: !disable ? cache.hasExpAccNotApprovedBool : disable,
            payCode1: !disable ? isRestricted : disable,
            payCode2: !disable ? isRestricted : disable,
            payCode3: !disable ? isRestricted : disable,
            payCode4: !disable ? isRestricted : disable,
            payCode5: !disable ? isRestricted : disable,
            payCode6: !disable ? isRestricted : disable,
            workRangeId: disable,
            payProjectCode: disable,
            payActivityCode: disable,
            taskCode: disable,
            abcProjectCode: disable,
            abcActivityCode: disable,
            activityNote: disable,
            requisitionCode: disable,
            otherClassification: disable,
            workOrder: disable,
            saveButton: disable,
            applyButton: disable,
            assignButton: disable,
            assignStartButton: disable,
            assignEndButton: disable,
            removePayCodeBtn: disable,
            addPayCodeBtn: disable,
            isRecalculateTime: disable,
            comments: disable,
            payrollComment: disable,
            purchaseOrder: disable,
            paidMileage: disable,
            chargedMileage: disable,
            bloc: blocDs,
            deleteButton: deleteButtonDs,
            punchStart: punchStartDs,
            punchEnd: punchEndDs,
            employeComment: employeCommentDs,
        }
    };

    const resetForm = () => {
        clearCache();
        setEmployees(null);
        setDepartments([]);
        setABCActivities([]);
        setABCProjects([]);
        setJobTitles([]);
        setPayProjects([]);
        setPayActivities([]);
        setTasksPay([]);
        setWorkorders([]);
        setPayCodesReg(null);
        setPayCodesOthers(null);
        setRequisitions([]);
        setShifts([]);
        setOtherClassifs([]);
        setShowAdditionalInfo(false);
        setShowElectronicDates(false);
        setIsDeleteBtnVisible(false);
        setShowSupCommentRequiredStar(true);
        setShowEndDateRequiredStar(true);
        setShowJobTitleRequiredStar(true);
        setShowEmployeeRequiredStar(true);
        setMessageConfirm(null);
        setDataPayCodesForm([]);
    };

    const refreshComboProperties = (newModel, response) => {
        refreshModel(newModel, response);
        refreshCombos(response);
    };    

    const refreshModel = (newModel, response) => {

        if (response.comboModel.isSelectedBloc) {
            newModel.bloc = response.comboModel.selectedBloc;
        }

        if (response.comboModel.isSelectedEmployee) {
            newModel.employeeCode = response.comboModel.selectedEmployee;
            newModel.epcdCrypt = response.comboModel.epcdCrypt;
        }

        if (response.comboModel.isSelectedShift) {
            newModel.workRangeId = response.comboModel.selectedShift;
        }

        if (response.comboModel.isSelectedDepartment) {
            newModel.departmentCode = response.comboModel.selectedDepartment;
        }

        if (response.comboModel.isSelectedJobTitle) {
            newModel.jobTitle = response.comboModel.selectedJobTitle;
        }

        if (response.comboModel.isSelectedAbcProject) {
            newModel.abcProjectCode = response.comboModel.selectedAbcProject;
        }

        if (response.comboModel.isSelectedAbcActivity) {
            newModel.abcActivityCode = response.comboModel.selectedAbcActivity;
        }

        if (response.comboModel.isSelectedPayProject) {
            newModel.payProjectCode = response.comboModel.selectedPayProject;
        }

        if (response.comboModel.isSelectedPayActivity) {
            newModel.payActivityCode = response.comboModel.selectedPayActivity;
        }

        if (response.comboModel.isSelectedTask) {
            newModel.taskCode = response.comboModel.selectedTask;
        }

        if (response.comboModel.isSelectedWorkorder) {
            newModel.workOrder = response.comboModel.selectedWorkorder;
        }

        if (response.comboModel.isSelectedOtherClassif) {
            newModel.otherClassification = response.comboModel.selectedOtherClassif;
        }

        if (response.comboModel.isSelectedRequisition) {
            newModel.requisitionCode = response.comboModel.selectedRequisition;
        }
    };

    const refreshCombos = (response) => {

        if (response.comboModel.isReloadEmployeeList) {
            setEmployees(response.comboModel.employees);
        }

        if (response.comboModel.isReloadDepartmentList) {
            setDepartments(response.comboModel.departments);
        }

        if (response.comboModel.isReloadABCActivityList) {
            setABCActivities(response.comboModel.abcActivities);
        }

        if (response.comboModel.isReloadABCProjectList) {
            setABCProjects(response.comboModel.abcProjects);
        }

        if (response.comboModel.isReloadJobTitleList) {
            setJobTitles(response.comboModel.jobTitles);
        }

        if (response.comboModel.isReloadPayProjectList) {
            setPayProjects(response.comboModel.payProjects);
        }

        if (response.comboModel.isReloadPayActivityList) {
            setPayActivities(response.comboModel.payActivities);
        }

        if (response.comboModel.isReloadTaskList) {
            setTasksPay(response.comboModel.tasksPay);
        }

        if (response.comboModel.isReloadWorkorderList) {
            setWorkorders(response.comboModel.workorders);
        }

        if (response.comboModel.isReloadRegularPayCodeList) {
            setPayCodesReg(response.comboModel.payCodesReg);
        }

        if (response.comboModel.isReloadPayCodeList) {
            setPayCodesOthers(response.comboModel.payCodesOthers);
        }

        if (response.comboModel.isReloadRequisitionList) {
            setRequisitions(response.comboModel.requisitions);
        }

        if (response.comboModel.isReloadShiftList) {
            setShifts(response.comboModel.shifts);
        }

        if (response.comboModel.isReloadOtherClassifList) {
            setOtherClassifs(response.comboModel.otherClassifs);
        }
    };    

    const eventChange = async(type, value) => {                
        setIsProcessing(true);
        const newModel = { ...model };
        const newDisableAttrbModel = { ...disableAttrbModel };        
        let response = null;

        newModel[type] = value;
        if (type === actionType.EMPLOYEE_CHANGE_EV.key) {
            newModel.epcdCrypt = null;
            if (value
                && employees?.length > 0) {
                const currentEmployee = employees.find(emp => emp.empC == value);
                if (currentEmployee) {
                    newModel.epcdCrypt = currentEmployee.epcdCrypt;
                }
            }
        }

        let index = 0;
        let eventKey = type;
        if (type.includes(actionType.PAYCODE_CHANGE_EV.key)) {
            index = type.replace(actionType.PAYCODE_CHANGE_EV.key, "");
            eventKey = type.replace(index, "");
            if (!isNullOrEmpty(value) && isNullOrEmpty(newModel[`duration${index}`])) {
                newModel[`duration${index}`] = 0;
            }
        }

        const prevIsHolidayPaycodeDefaults = cache.isHolidayPaycodeDefaults;
        const currentIsHolidayPaycodeDefaults = isPayCodeHoliday(newModel);
        const doLoadCache = config.doManageHolidayPaycode && prevIsHolidayPaycodeDefaults && !currentIsHolidayPaycodeDefaults;
        
        if (hasServerSideEventManagement(eventKey)) {

            // manageHolidayPaycode - additional
            if (eventKey === actionType.PAYCODE_CHANGE_EV.key)
            {
                if (doLoadCache) {

                    if (config.isUseAbc) {
                        if (cache[pKeys.ABC_PROJECT].isCached) {
                            newModel.abcProjectCode = cache[pKeys.ABC_PROJECT].value;
                        }

                        if (cache[pKeys.ABC_ACTIVITY].isCached) {
                            newModel.abcActivityCode = cache[pKeys.ABC_ACTIVITY].value;
                        }
                    }

                    if (config.isProjectEntry && cache[pKeys.PAY_PROJECT].isCached) {
                        newModel.payProjectCode = cache[pKeys.PAY_PROJECT].value;
                    }

                    if (config.isActivityEntry && cache[pKeys.PAY_ACTIVITY].isCached) {
                        newModel.payActivityCode = cache[pKeys.PAY_ACTIVITY].value;
                    }

                    if (config.isTaskEntry && cache[pKeys.TASK].isCached) {
                        newModel.taskCode = cache[pKeys.TASK].value;
                    }
                }
            }

            newModel.abcActivityCode = !isNullOrEmpty(newModel.abcActivityCode) ? parseInt(newModel.abcActivityCode) : null;
            newModel.workRangeId = !isNullOrEmpty(newModel.workRangeId) ? parseInt(newModel.workRangeId) : 0;

            response = await refreshFormSrv(PageSourceCode.PAGE_PUNCH_EDIT,
                typeMode,
                actionType.getIndex(eventKey),                
                newModel,
                index,
                prevIsHolidayPaycodeDefaults,
                cache.currentEmploymentStatus);
        }

        if (hasServerSideEventManagement(eventKey) && response?.status !== StatusCode.STATUS_SUCCESS) {
            alertRef.current.showMessageError(i18next.t('error:generic-error'));
            setIsProcessing(false);
            return;
        }

        if (response?.status === StatusCode.STATUS_SUCCESS) {

            // setCache => newModel values before form refresh
            manageHolidayPaycode(newModel, newDisableAttrbModel, {
                isHolidayPaycodeDefaults: response.comboModel.isHolidayPaycodeDefaults,
                isChangeHolidayPaycodeDefaults: response.comboModel.isChangeHolidayPaycodeDefaults,
                payProjectsList: response.comboModel.isReloadPayProjectList ? response.comboModel.payProjects : payProjects,
                payActivitiesList: response.comboModel.isReloadPayActivityList ? response.comboModel.payActivities : payActivities,
                tasksPayList: response.comboModel.isReloadTaskList ? response.comboModel.tasksPay : tasksPay,
            }, value, index, eventKey === actionType.PAYCODE_CHANGE_EV.key, false);

            // Refresh cache
            refreshPoolCache(
                response.comboModel.isSelectedDepartment ? response.comboModel.selectedDepartment : newModel.departmentCode,
                response.comboModel.isReloadDepartmentList ? response.comboModel.departments : newModel.departments
            );

            if (response.comboModel.isChangeHolidayPaycodeDefaults) {
                cache.isHolidayPaycodeDefaults = response.comboModel.isHolidayPaycodeDefaults;
            }

            if (response.comboModel.isChangeEmploymentStatus) {
                cache.currentEmploymentStatus = response.comboModel.currentEmploymentStatus;
            }

            cache.isPunchRestricted = response.isPunchRestricted;

            // Refresh form
            refreshComboProperties(newModel, response);
        }

        switch (eventKey) {
            case actionType.PAYCODE_CHANGE_EV.key:
                // loadFromCache
                manageHolidayPaycode(newModel, newDisableAttrbModel, {
                    isHolidayPaycodeDefaults: response.comboModel.isHolidayPaycodeDefaults,
                    isChangeHolidayPaycodeDefaults: response.comboModel.isChangeHolidayPaycodeDefaults,
                    payProjectsList: response.comboModel.isReloadPayProjectList ? response.comboModel.payProjects : payProjects,
                    payActivitiesList: response.comboModel.isReloadPayActivityList ? response.comboModel.payActivities : payActivities,
                    tasksPayList: response.comboModel.isReloadTaskList ? response.comboModel.tasksPay : tasksPay,
                }, value, index, false, eventKey === actionType.PAYCODE_CHANGE_EV.key);

                // manageHolidayPaycode - additional 
                if (doLoadCache) {
                    if (!config.isPayProjectLoadedFromWorkorder && config.isLinkProjectActivityTaskP) {
                        if (config.isProjectEntry && config.isActivityEntry && isNullOrEmpty(newModel.payProjectCode)) {
                            newModel.payActivityCode = null;
                            setPayActivities([]);
                        }

                        if (config.isActivityEntry && config.isTaskEntry && isNullOrEmpty(newModel.payActivityCode)) {
                            newModel.taskCode = null;
                            setTasksPay([]);
                        }
                    }
                }
                break;
            case actionType.STARTDATE_CHANGE_EV.key:
                fillProjectComboBoxByWorkorder(newModel, newDisableAttrbModel);
                if (cache.isFormCanBeEnabled && !isNullOrEmpty(newModel.abcProjectCode)) {
                    newDisableAttrbModel.payProjectCode = newDisableAttrbModel.abcProjectCode = false;
                }
                const startDate = toLocalDate(newModel.realStartTime);
                const endDate = toLocalDate(newModel.realEndTime);
                if (startDate && endDate) {
                    const dtEnd = (endDate.getHours() >= startDate.getHours()) || (typeMode === mode.ADD) ? startDate : startDate.setDate(startDate.getDate() + 1);
                    newModel.realEndTime = new Date(dtEnd.getFullYear(), dtEnd.getMonth(), dtEnd.getDate(), endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
                }
                break;
            case actionType.ENDDATE_CHANGE_EV.key:
                if (!newModel.realStartTime) {
                    fillProjectComboBoxByWorkorder(newModel, newDisableAttrbModel);
                }
                break;
            case actionType.EMPLOYEE_CHANGE_EV.key:
                if (response.empInfos) {
                    checkDepartementInPool(newModel, newDisableAttrbModel);
                }
                break;
            case actionType.DEPARTMENT_CHANGE_EV.key:
                checkDepartementInPool(newModel, newDisableAttrbModel);
                break;
            case actionType.WORKORDER_CHANGE_EV.key:
                fillProjectComboBoxByWorkorder(newModel, newDisableAttrbModel);
                break;
            case actionType.ISOVERTIMEAFTER_EV.key:
                if (typeMode !== mode.ADD && newModel.origin == punchSource.SPREADSHEET) {
                    alertRef.current.showMessageError(i18next.t('punchApproval:message.cannotCalculateOvertimeSpreadsheet'));
                    newModel.isOvertimeAfter = !newModel.isOvertimeAfter;
                }
                break;
            case actionType.ISOVERTIMEBEFORE_EV.key:
                if (typeMode !== mode.ADD && newModel.origin == punchSource.SPREADSHEET) {
                    alertRef.current.showMessageError(i18next.t('punchApproval:message.cannotCalculateOvertimeSpreadsheet'));
                    newModel.isOvertimeBefore = !newModel.isOvertimeBefore;
                }
                break;
            case actionType.ISAPPROVED_EV.key:
                setShowEndDateRequiredStar(newModel.isApproved || (newModel.punchEnd && newModel.insertionType == punchInsertionType.Punch));
                break;
        }

        setModel(newModel);
        setDisableAttrbModel(newDisableAttrbModel);
        setIsProcessing(false);
    };

    const validateForm = (newModel) => {

        let message = '';
        let error = false;
        const maxhourdiff = 24;
        const realStartTime = toLocalDate(newModel.realStartTime);
        const realEndTime = toLocalDate(newModel.realEndTime);

        // Difference in milliseconds
        let diff = 0;
        if (realStartTime && realEndTime) {
            diff = Date.parse(realEndTime) - Date.parse(realStartTime);
        }

        // Get total hours
        const totalHours = diff / 1000 / 60 / 60;
        const duration1 = toDecimal(newModel.duration1);
        const duration2 = toDecimal(newModel.duration2);
        const duration3 = toDecimal(newModel.duration3);
        const duration4 = toDecimal(newModel.duration4);
        const duration5 = toDecimal(newModel.duration5);
        const duration6 = toDecimal(newModel.duration6);

        if (isNullOrEmpty(newModel.employeeCode)) { //Required
            message = i18next.t('punchApproval:form.validation-messages.employeeCode');
            error = true;
        } else if (!realStartTime) { //Required
            message = i18next.t('punchApproval:form.validation-messages.realStartTime');
            error = true;
        } else if ((!realEndTime && (newModel.isApproved || (newModel.punchEnd && newModel.insertionType === punchInsertionType.Punch)))
            || (realEndTime && (realStartTime > realEndTime || totalHours > maxhourdiff))) { //RequiredDateGreaterThanIF
            message = i18next.t('punchApproval:form.validation-messages.realEndTime');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode1)) { //Required
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'),1);
            error = true;
        } else if (isNullOrEmpty(duration1) || duration1 < 0 || duration1 > 25) { //Required + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode2) && !isNullOrEmpty(newModel.duration2) && duration2 > 0) { //RequiredSubCodeIF
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'),2);
            error = true;
        } else if (!isNullOrEmpty(newModel.payCode2) && (isNullOrEmpty(newModel.duration2) || !duration2 || duration2 < 0 || duration2 > 25)) { //RequiredPunchTimeIF + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode3) && !isNullOrEmpty(newModel.duration3) && duration3 > 0) { //RequiredSubCodeIF
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'),3);
            error = true;
        } else if (!isNullOrEmpty(newModel.payCode3) && (isNullOrEmpty(newModel.duration3) || !duration3 || duration3 < 0 || duration3 > 25)) { //RequiredPunchTimeIF + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode4) && !isNullOrEmpty(newModel.duration4) && duration4 > 0) { //RequiredSubCodeIF
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'),4);
            error = true;
        } else if (!isNullOrEmpty(newModel.payCode4) && (isNullOrEmpty(newModel.duration4) || !duration4 || duration4 < 0 || duration4 > 25)) { //RequiredPunchTimeIF + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode5) && !isNullOrEmpty(newModel.duration5) && duration5 > 0) { //RequiredSubCodeIF
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'),5);
            error = true;
        } else if (!isNullOrEmpty(newModel.payCode5) && (isNullOrEmpty(newModel.duration5) || !duration5 || duration5 < 0 || duration5 > 25)) { //RequiredPunchTimeIF + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if (isNullOrEmpty(newModel.payCode6) && !isNullOrEmpty(newModel.duration6) && duration6 > 0) { //RequiredSubCodeIF
            message = format(i18next.t('punchApproval:form.validation-messages.payCode'), 6);
            error = true;
        } else if (!isNullOrEmpty(newModel.payCode6) && (isNullOrEmpty(newModel.duration6) || !duration6 || duration6 < 0 || duration6 > 25)) { //RequiredPunchTimeIF + [Range(0, 24)]
            message = i18next.t('punchApproval:form.validation-messages.duration');
            error = true;
        } else if ((config.commentForApprovOnPunch === 'S' || config.commentForApprovOnPunch === 'D') && isNullOrEmpty(newModel.comments)) { //RequiredIFIsRequiredCommentForApprovOnPunch
            message = i18next.t('punchApproval:form.validation-messages.comments');
            error = true;
        } else if (config.isUseWorkorderInPunch && isNullOrEmpty(newModel.workOrder)) { //RequiredIFUseWorkorder
            message = i18next.t('punchApproval:form.validation-messages.workOrder');
            error = true;
        } else if (isNullOrEmpty(newModel.departmentCode)) { //Required
            message = i18next.t('punchApproval:form.validation-messages.departmentCode');
            error = true;
        } else if (config.isJobTitleEntry && isNullOrEmpty(newModel.jobTitle)) { //RequiredIFJobtitleEntry
            message = i18next.t('punchApproval:form.validation-messages.jobTitle');
            error = true;
        } else if (config.isProjectEntry && !config.isPayProjectLoadedFromWorkorder && isNullOrEmpty(newModel.payProjectCode)) { //RequiredIFProjectEntry
            message = i18next.t('punchApproval:form.validation-messages.projectCode');
            error = true;
        } else if (config.isActivityEntry && !config.isPayProjectLoadedFromWorkorder && isNullOrEmpty(newModel.payActivityCode)) { //RequiredIFActivityEntry
            message = i18next.t('punchApproval:form.validation-messages.activityPay');
            error = true;
        } else if (config.isUseAbc && isNullOrEmpty(newModel.abcProjectCode)) { // RequiredIFUseAbc
            message = i18next.t('punchApproval:form.validation-messages.abcProjectCode');
            error = true;
        } else if (config.isUseAbc && isNullOrEmpty(newModel.abcActivityCode)) {
            message = i18next.t('punchApproval:form.validation-messages.activityCode');
            error = true;
        } else if (config.isUseAbc && config.isUseRequisitionCodeOnPunch && isNullOrEmpty(newModel.requisitionCode)) { //RequiredIFUseRequisitionCodeOnPunch
            message = i18next.t('punchApproval:form.validation-messages.requisitionCode');
            error = true;
        } else if (config.isTaskEntry && !config.isPayProjectLoadedFromWorkorder && isNullOrEmpty(newModel.taskCode)) { //RequiredIFTaskEntry
            message = i18next.t('punchApproval:form.validation-messages.taskCode');
            error = true;
        } else if (config.isUseOtherClassification && isNullOrEmpty(newModel.otherClassification)) { //RequiredIFUseOtherClassification
            message = i18next.t('punchApproval:form.validation-messages.otherClassification');
            error = true;
        }

        if (error) {
            document.querySelector('#divPunchForm').scrollTo(0, 0);
            alertRef.current.showMessageError(message);
        }

        return !error;
    };

    const refreshPoolCache = (deptCode, deptList) => {
        cache.isPool = false;
        if (deptCode && deptList?.length > 0) {
            const currentDepartment = deptList.find(dept => dept.depC == deptCode);
            if (currentDepartment) {
                cache.isPool = parseBool(currentDepartment.pool);
            }
        }
    };

    const checkDepartementInPool = (newModel, newDisableAttrbModel) => {
        if (cache.hasExpAccNotApprovedBool || cache.isPool) {
            newModel.isApproved = false;
            newDisableAttrbModel.isApproved = true;
        }
        else if (typeMode !== mode.EDIT || !cache.hasExpAccNotApprovedBool) {
            newDisableAttrbModel.isApproved = !cache.isFormCanBeEnabled;
        }
    };

    const isPayCodeHoliday = (newModel = null, paycode = null, paycodeIndex = 0) => {

        if (paycodeIndex !== 0) {

            let paycodeGroup = [];

            switch (paycodeIndex) {
                case 1:
                    if (paycode
                        && payCodesReg?.length > 0) {
                        const grp = payCodesReg.find(payCd => payCd.sc == paycode);
                        if (grp) {
                            paycodeGroup = grp.acG.split(',');
                        }
                    }
                    break;
                default:
                    if (paycode
                        && payCodesOthers?.length > 0) {
                        const grp = payCodesOthers.find(payCd => payCd.sc == paycode);
                        if (grp) {
                            paycodeGroup = grp.acG.split(',');
                        }
                    }
                    break;
            }

            if (paycodeGroup.length > 0) {
                return paycodeGroup.includes(subCodeGroupCode.HTPABS)
                    || paycodeGroup.includes(subCodeGroupCode.HTPVAC);
            }

            return false;
        }

        const payCodeGroups = [];
        const payCode1 = newModel.payCode1;
        const payCode2 = newModel.payCode2;
        const payCode3 = newModel.payCode3;
        const payCode4 = newModel.payCode4;
        const payCode5 = newModel.payCode5;
        const payCode6 = newModel.payCode6;

        if (payCode1
            && payCodesReg?.length > 0) {
            const currentPayCodeReg = payCodesReg.find(payCd => payCd.sc == payCode1);
            if (currentPayCodeReg) {
                payCodeGroups.push(...currentPayCodeReg.acG.split(','));
            }
        }

        if (payCodesOthers?.length > 0 &&
            (
                payCode2
                || payCode3
                || payCode4
                || payCode5
                || payCode6
            )
        ) {
            payCodesOthers.filter(payCd => payCd.sc == payCode2
                || payCd.sc == payCode3
                || payCd.sc == payCode4
                || payCd.sc == payCode5
                || payCd.sc == payCode6).forEach(currentPayCdOther => {
                    payCodeGroups.push(...currentPayCdOther.acG.split(','));
                });;
        }

        return payCodeGroups.includes(subCodeGroupCode.HTPABS)
            || payCodeGroups.includes(subCodeGroupCode.HTPVAC);
    };    

    const hasServerSideEventManagement = (key) => {
        switch (key) {
            case actionType.EMPLOYEE_CHANGE_EV.key:
            case actionType.STARTDATE_CHANGE_EV.key:            
            case actionType.DEPARTMENT_CHANGE_EV.key:
            case actionType.PAYCODE_CHANGE_EV.key:
            case actionType.ABCPROJECT_CHANGE_EV.key:
            case actionType.WORKORDER_CHANGE_EV.key:
                return true;
            case actionType.ENDDATE_CHANGE_EV.key:
                return config.isUseWorkorderInPunch;
            case actionType.JOBTITLE_CHANGE_EV.key:
                return config.isDisplaySubcodeProfileByJobTitle;
            case actionType.PAYPROJECT_CHANGE_EV.key:
            case actionType.PAYACTIVITY_CHANGE_EV.key:
                return config.isLinkProjectActivityTaskP;
            default:
                return false;
        }
    };

    const getRealDateTime = (date, startTime) => {
        startTime = startTime ?? 0;
        let dt = toLocalDate(date);
        let hours = parseInt(startTime);
        startTime = parseFloat(startTime);
        let mins = startTime - hours;
        mins = parseInt(mins.toFixed(2) * 100);
        dt.setHours(dt.getHours() + hours);
        dt.setMinutes(dt.getMinutes() + mins);
        return toLocalDate(dt);
    };

    const fillProjectComboBoxByWorkorder = (newModel, newDisableAttrbModel) => {

        if (!config.isUseWorkorderInPunch || cache.isHolidayPaycodeDefaults) {
            return;
        }        

        if (isNullOrEmpty(newModel.workOrder)) {
            if (cache.isFormCanBeEnabled) {
                if (config.isProjAssocWorkorderInPunch) {
                    newDisableAttrbModel.payProjectCode = newDisableAttrbModel.abcProjectCode = false;
                }

                if (config.isActivityAssocWorkorderInPunch) {
                    newDisableAttrbModel.abcActivityCode = false;
                }
            }
        } else if (config.isProjAssocWorkorderInPunch) {
            newDisableAttrbModel.payProjectCode = newDisableAttrbModel.abcProjectCode = true;
            if (config.isActivityAssocWorkorderInPunch && !isNullOrEmpty(newModel.abcProjectCode)) {
                newDisableAttrbModel.abcActivityCode = true;
            } else if (cache.isFormCanBeEnabled) {
                newDisableAttrbModel.abcActivityCode = false;
            }
        }
    };

    const onClose = async (reload = true, message = false, isError = false) => {
        setIsProcessing(false);
        setTimeout(function () {
            if (reload && props.openDetail === true) {
                props.setIsReload(true);
            }
            props.handleClose(reload, isError);
        }, message ? 1000 : 0);
    };

    const clearCache = () => {
        cache.isOpenDatePickerView = false;
        cache.fullName = null;
        cache.distributionType = null;
        cache.currentEmploymentStatus = null;
        cache.isPool = false;
        cache.adjustmentPunch = false;
        cache.isPunchRestricted = false;
        cache.isFormCanBeEnabled = true;
        cache.isHolidayPaycodeDefaults = false;
        cache.hasExpAccNotApprovedBool = false;
        cache[pKeys.ABC_PROJECT].isCached = false;
        cache[pKeys.ABC_PROJECT].value = null;
        cache[pKeys.ABC_PROJECT].enable = true;
        cache[pKeys.ABC_ACTIVITY].isCached = false;
        cache[pKeys.ABC_ACTIVITY].value = null;
        cache[pKeys.ABC_ACTIVITY].enable = true;
        cache[pKeys.PAY_PROJECT].isCached = false;
        cache[pKeys.PAY_PROJECT].value = null;
        cache[pKeys.PAY_PROJECT].enable = true;
        cache[pKeys.PAY_ACTIVITY].isCached = false;
        cache[pKeys.PAY_ACTIVITY].value = null;
        cache[pKeys.PAY_ACTIVITY].enable = true;
        cache[pKeys.TASK].isCached = false;
        cache[pKeys.TASK].value = null;
        cache[pKeys.TASK].enable = true;
    }

    const clearPayCodeHolidayCache = (key = null) => {
        if (key === null) {
            cache[pKeys.ABC_PROJECT].isCached = false;
            cache[pKeys.ABC_PROJECT].value = null;
            cache[pKeys.ABC_PROJECT].enable = true;
            cache[pKeys.ABC_ACTIVITY].isCached = false;
            cache[pKeys.ABC_ACTIVITY].value = null;
            cache[pKeys.ABC_ACTIVITY].enable = true;
            cache[pKeys.PAY_PROJECT].isCached = false;
            cache[pKeys.PAY_PROJECT].value = null;
            cache[pKeys.PAY_PROJECT].enable = true;
            cache[pKeys.PAY_ACTIVITY].isCached = false;
            cache[pKeys.PAY_ACTIVITY].value = null;
            cache[pKeys.PAY_ACTIVITY].enable = true;
            cache[pKeys.TASK].isCached = false;
            cache[pKeys.TASK].value = null;
            cache[pKeys.TASK].enable = true;
        } else {
            cache[key].isCached = false;
            cache[key].value = null;
            cache[key].enable = true;
        }
    }

    const activateCache = (newModel, newDisableAttrbModel, key, prevIsHolidayPaycodeDefaults) => {
        if (!prevIsHolidayPaycodeDefaults) {
            cache[key].value = newModel[key];
            cache[key].enable = !newDisableAttrbModel[key];
            cache[key].isCached = true;
        }        
    }

    const manageHolidayPaycode = (newModel, newDisableAttrbModel, newLists, paycode = null, paycodeIndex = 0, setCache = true, loadFromCache = true) => {        

        if (!config.doManageHolidayPaycode || (!setCache && !loadFromCache)) {
            return;
        }
        
        const keepHolidayPaycodeDefaults = newLists.isHolidayPaycodeDefaults;        
        const prevIsHolidayPaycodeDefaults = newLists.isChangeHolidayPaycodeDefaults === true ?
            !keepHolidayPaycodeDefaults :
            keepHolidayPaycodeDefaults;

        let currentIsHolidayPaycodeDefaults = newLists.isHolidayPaycodeDefaults;
        if (paycodeIndex != 0) {
            currentIsHolidayPaycodeDefaults = isPayCodeHoliday(null, paycode, paycodeIndex);
        }        

        if (setCache && currentIsHolidayPaycodeDefaults) {

            if (!prevIsHolidayPaycodeDefaults) {
                clearPayCodeHolidayCache();
            }

            if (config.isUseAbc) {
                if (!isNullOrEmpty(config.defaultABCProjectHoliday)) {
                    activateCache(newModel, newDisableAttrbModel, pKeys.ABC_PROJECT, prevIsHolidayPaycodeDefaults);
                    newDisableAttrbModel.abcProjectCode = true;
                }

                if (!isNullOrEmpty(config.defaultABCActivityHoliday)) {
                    activateCache(newModel, newDisableAttrbModel, pKeys.ABC_ACTIVITY, prevIsHolidayPaycodeDefaults);
                    newDisableAttrbModel.abcActivityCode = true;
                }
            }

            if (config.isProjectEntry && !isNullOrEmpty(config.defaultPayProjectHoliday)) {
                activateCache(newModel, newDisableAttrbModel, pKeys.PAY_PROJECT, prevIsHolidayPaycodeDefaults);
                const payProjectCode = newLists.payProjectsList.find(prj => prj.prC == config.defaultPayProjectHoliday)?.prC;                
                if (payProjectCode === config.defaultPayProjectHoliday) {
                    newDisableAttrbModel.payProjectCode = true;
                } else if (cache.isFormCanBeEnabled) {
                    newDisableAttrbModel.payProjectCode = false;
                }
            }

            if (config.isActivityEntry && !isNullOrEmpty(config.defaultPayActivityHoliday)) {
                activateCache(newModel, newDisableAttrbModel, pKeys.PAY_ACTIVITY, prevIsHolidayPaycodeDefaults);
                const payActivityCode = newLists.payActivitiesList.find(act => act.actC == config.defaultPayActivityHoliday)?.actC;
                if (payActivityCode == config.defaultPayActivityHoliday) {
                    newDisableAttrbModel.payActivityCode = true;
                } else if (cache.isFormCanBeEnabled) {
                    newDisableAttrbModel.payActivityCode = false;
                }
            }

            if (config.isTaskEntry && !isNullOrEmpty(config.defaultPayTaskHoliday)) {
                activateCache(newModel, newDisableAttrbModel, pKeys.TASK, prevIsHolidayPaycodeDefaults);
                const taskCode = newLists.tasksPayList.find(tsk => tsk.tkC == config.defaultPayTaskHoliday)?.tkC;                    
                if (taskCode == config.defaultPayTaskHoliday) {
                    newDisableAttrbModel.taskCode = true;
                }
            }
        } else if (loadFromCache && !keepHolidayPaycodeDefaults && prevIsHolidayPaycodeDefaults) {

            if (config.isUseAbc) {
                if (cache[pKeys.ABC_PROJECT].isCached) {
                    if (cache.isFormCanBeEnabled) {
                        newDisableAttrbModel.abcProjectCode = !cache[pKeys.ABC_PROJECT].enable;
                    }
                }

                if (cache[pKeys.ABC_ACTIVITY].isCached) {
                    if (cache.isFormCanBeEnabled) {
                        newDisableAttrbModel.abcActivityCode = !cache[pKeys.ABC_ACTIVITY].enable;
                    }
                }
            }

            if (config.isProjectEntry && cache[pKeys.PAY_PROJECT].isCached) {
                if (cache.isFormCanBeEnabled) {
                    newDisableAttrbModel.payProjectCode = !cache[pKeys.PAY_PROJECT].enable;
                }
            }

            if (config.isActivityEntry && cache[pKeys.PAY_ACTIVITY].isCached) {
                if (cache.isFormCanBeEnabled) {
                    newDisableAttrbModel.payActivityCode = !cache[pKeys.PAY_ACTIVITY].enable;
                }
            }

            if (config.isTaskEntry && cache[pKeys.TASK].isCached) {
                if (cache.isFormCanBeEnabled) {
                    newDisableAttrbModel.taskCode = !cache[pKeys.TASK].enable;
                }
            }
        }
    };

    /**
     * 
     * Manage Actions / Events
     */
    const onBlurEvent = async (e) => {
        const name = e.target.name;
        const value = e.target.value;

        if (!cache.isOpenDatePickerView
            && (name === actionType.STARTDATE_CHANGE_EV.key
                || name === actionType.ENDDATE_CHANGE_EV.key)) {
            e.stopPropagation();
            eventChange(name, convertToDate(value));
        }        
    };

    const onOpenEvent = (e) => {
        cache.isOpenDatePickerView = true;
    };

    const onCloseEvent = (e) => {
        cache.isOpenDatePickerView = false;
    };

    const handleChange = (e) => {

        const name = e.target.name;
        let value = e.target.value;

        if (!cache.isOpenDatePickerView
            && (name === actionType.STARTDATE_CHANGE_EV.key
            || name === actionType.ENDDATE_CHANGE_EV.key)) {
            if (value) {
                setModel(prevModel => {
                    const newModel = { ...prevModel };
                    newModel[name] = value;
                    return newModel;
                });
            }
            return;
        }

        if (
            (name === pKeys.IS_APPROVED)
            || (name === pKeys.IS_OVERTIMEBEFORE)
            || (name === pKeys.IS_OVERTIMEAFTER)
            || (name === pKeys.IS_RECALCULATE_TIME)
        ) {
            value = e.target.checked;
        }

        eventChange(name, value);
    };

    const handleOpen = (e) => {
        const startDate = toLocalDate(model.realStartTime);
        const endDate = toLocalDate(model.realEndTime);

        if ((startDate != null && endDate == null)
            || (startDate > endDate)) {
            setModel(prevModel => {
                const newModel = { ...prevModel };
                newModel.realEndTime = model.realStartTime;
                return newModel;
            });
        }
    };

    const removeItem = (indexToRemove) => {
        const updated = dataPayCodesForm
            .filter(item => item.index !== indexToRemove)
            .map((item, i) => ({ ...item, index: i + 1 }));

        setModel(prevModel => {
            const newModel = { ...prevModel };
            newModel[`payCode${indexToRemove}`] = null;
            newModel[`duration${indexToRemove}`] = null;
            return newModel;
        });

        setDataPayCodesForm(updated);
    };

    const addItem = () => {
        const newItem = {
            index: dataPayCodesForm.length + 1,
            payCode: '',
            duration: '',
        };
        setDataPayCodesForm([...dataPayCodesForm, newItem]);
    };

    const editItem = (e, indexToEdit, type) => {
        const updated = dataPayCodesForm.map((item) => {
            
            if (item.index === indexToEdit && type == 'payCode' && !isNullOrEmpty(e.target.value) && isNullOrEmpty(item.duration)) {
                item.duration = 0;
            }

            return item.index === indexToEdit ?
                type == 'payCode' ?
                    {
                        ...item,
                        payCode: e.target.value
                    } :
                    {
                        ...item,
                        duration: e.target.value
                    }
                : item
        });

        setDataPayCodesForm(updated);
        handleChange(e);
    };

    const handleFocus = (event) => {
        // this should be good on desktop and android
        //event.target.select();

        // workaround to make it work in iOS
        event.target.setSelectionRange(0, event.target.value.length);
    };

    const handleAdditionalInfoClick = () => {
        setShowAdditionalInfo(true);
    };

    const assignScheduleStart = () => {
        if (cache.isPunchRestricted
            || isNullOrEmpty(model.workRangeId)
            || model.workRangeId === 0
            || shifts.length === 0
        ) {
            return;
        }

        const currentShift = shifts.find(sft => sft.shId == model.workRangeId);
        if (!currentShift) {
            return;
        }

        setModel(prevModel => {
            const newModel = { ...prevModel };
            const startDate = newModel.realStartTime ?? new Date();
            startDate.setHours(0, 0, 0, 0);
            newModel.realStartTime = getRealDateTime(startDate, currentShift.strHr);
            return newModel;
        });
    };

    const assignScheduleEnd = () => {
        if (cache.isPunchRestricted
            || isNullOrEmpty(model.workRangeId)
            || model.workRangeId === 0
            || shifts.length === 0
        ) {
            return;
        }

        const currentShift = shifts.find(sft => sft.shId == model.workRangeId);
        if (!currentShift) {
            return;
        }

        setModel(prevModel => {
            const newModel = { ...prevModel };
            const startDate = newModel.realStartTime ?? new Date();
            startDate.setHours(0, 0, 0, 0);
            const sDate = getRealDateTime(startDate, currentShift.strHr);
            const eDate = getRealDateTime(startDate, currentShift.endHr);

            if (eDate < sDate) {
                eDate.setDate(eDate.getDate() + 1);
            }

            newModel.realEndTime = eDate;
            return newModel;
        });
    };

    const assignSchedule = () => {
        if (cache.isPunchRestricted
            || isNullOrEmpty(model.workRangeId)
            || model.workRangeId === 0
            || shifts.length === 0
        ) {
            return;
        }

        const currentShift = shifts.find(sft => sft.shId == model.workRangeId);
        if (!currentShift) {
            return;
        }

        setModel(prevModel => {
            const newModel = { ...prevModel };
            const startDate = newModel.realStartTime ?? new Date();
            startDate.setHours(0, 0, 0, 0);
            const sDate = getRealDateTime(startDate, currentShift.strHr);
            const eDate = getRealDateTime(startDate, currentShift.endHr);

            if (eDate < sDate) {
                eDate.setDate(eDate.getDate() + 1);
            }

            newModel.realStartTime = sDate;
            newModel.realEndTime = eDate;
            return newModel;
        });
    };

    const handleHideAdditionalInfo = () => {
        setShowAdditionalInfo(false);
    };

    const handleSaveClick = async (close = true) => {

        if (cache.isPunchRestricted) {
            return;
        }
        
        const newModel = { ...model };

        if (abcActivities.length === 0) {
            newModel.abcActivityCode = null;
            const response = await refreshFormSrv(PageSourceCode.PAGE_PUNCH_EDIT,
                typeMode,
                actionType.ABCACTIVITY_FILL.index,
                newModel,
                0,
                cache.isHolidayPaycodeDefaults,
                cache.currentEmploymentStatus);

            if (response?.status === StatusCode.STATUS_SUCCESS) {
                newModel.abcActivityCode = response.selectedAbcActivity;
                setABCActivities(response.abcActivities);
            } else {
                alertRef.current.showMessageError(i18next.t('error:generic-error'));
                return;
            }
        }

        if (newModel.workOrder && workorders?.length > 0) {
            const currentWorkorder = workorders.find(wk => wk.woCode == newModel.workOrder);
            if (currentWorkorder) {
                newModel.workOrderId = currentWorkorder.woId;
            }
        }
        
        setModel({ ...newModel });

        if (validateForm(newModel)) {

            setOpenConfirm(false);
            setIsProcessing(true);

            let response = await savePunch(config, newModel);
            let message = i18next.t('error:generic-error');

            if (response?.status === StatusCode.STATUS_SUCCESS) {

                message = response.message;

                if (response.success === true
                    || response.warning === true) {
                    
                    if (!close && !message) {
                        message = i18next.t('punchApproval:message.add-success');
                        if (typeMode === mode.COPY) {
                            message = i18next.t('punchApproval:message.copy-success');
                        } else if (typeMode === mode.EDIT) {
                            message = i18next.t('punchApproval:message.edit-success');
                        }
                    }

                    if (message) {
                        if (response.success === true && !response.warning) {
                            alertRef.current.showMessageSuccess(message);
                        } else {
                            alertRef.current.showMessageWarning(message);
                        }
                    }
                    
                    if (close) {
                        onClose(true, response.message);
                    }

                    return response.obj.punchCrypt;
                }

                if (response.validationError === true) {
                    message = i18next.t('punchApproval:message.failureValidation');
                } else if (response.error === true) {
                    if (!message) {
                        message = i18next.t('punchApproval:message.add-error');
                        if (typeMode === mode.COPY) {
                            message = i18next.t('punchApproval:message.copy-error');
                        } else if (typeMode === mode.EDIT) {
                            message = i18next.t('punchApproval:message.edit-error');
                        }
                    }
                }
            }

            alertRef.current.showMessageError(message);
            setIsProcessing(false);            
            return;
        }

        return;
    };

    const handleApplyClick = async () => {
        const punchCrypt = await handleSaveClick(false);        
        if (!punchCrypt) {
            return;
        }
        props.setIsReload(true);        
        setTypeMode(mode.EDIT);
        onLoadForm(punchCrypt);
    };

    const handleOpenConfirmDelete = () => {
        setMessageConfirm(format(i18next.t('punchApproval:message.confirmation-delete'), cache.fullName, formatDate(model.realStartTime)));
        setOpenConfirm(true);
    };

    const handleDeleteClick = async () => {

        setOpenConfirm(false);
        setIsProcessing(true);

        let message = i18next.t('error:generic-error');
        let response = await deletePunch(PageSourceCode.PAGE_PUNCH_EDIT, typeMode, model.punchEnCrypt, model.epcdCrypt);
        
        if (response?.status === StatusCode.STATUS_SUCCESS) {
            if (response.success === true) {
                onClose();
                return;
            } else {
                message = response?.message ?? i18next.t('punchApproval:message.delete-error');
            }
        }

        alertRef.current.showMessageError(message);
        setIsProcessing(false);
    };

    const handleCloseConfirm = () => {
        setOpenConfirm(false);
    }

    /**
     * 
     * Rendering
     */
    return (
        <LoadingLayer
            isActive={isProcessing}
            className={styles.divLoading}
            text={i18next.t('punch:form.loading')}
            spinner={<BeatLoader />}
        >
            <div id="divPunchForm" className={styles.divPunchForm}>
                <FormControl className={`${classes.customRoot} ${styles.switchComponent}`}>
                    <FormControlLabel
                        control={
                            <Switch
                                disabled={disableAttrbModel.isApproved}
                                checked={model.isApproved}
                                onChange={handleChange}
                                name="isApproved"
                                variant="success"
                            />
                        }
                        label={i18next.t("punchApproval:form.approved")}
                        labelPlacement="start"
                    />
                </FormControl>

                <FormControl fullWidth>
                    <div className={styles.divFormSelect}>
                        <InputLabel className={showEmployeeRequiredStar ? "required" : ""} id="label-employee">{i18next.t('punchApproval:form.employee')}</InputLabel>
                        <Select
                            labelId="label-employee"
                            id="employeeCode"
                            name="employeeCode"
                            disabled={disableAttrbModel.employeeCode}
                            value={model.employeeCode || ''}
                            onChange={handleChange}
                            className={classes.select}
                            MenuProps={{ classes: { paper: styles.selectList } }}
                        >
                            {employees && employees.map((obj, index) =>
                                <MenuItem key={index} className={styles.selectItem} value={obj.empC}>{`${obj.empC} - ${obj.empN}`}</MenuItem>
                            )}
                        </Select>
                    </div>
                </FormControl>

                <div className={`${classes.divInput_80} ${styles.divDate}`} style={{ marginTop: '10px' }} ref={el => (_el = el)}>                        
                    <LocalizationProvider language={locale}>
                        <IntlProvider locale={locale}>
                            <InputLabel className={"required"} htmlFor="realStartTime">{i18next.t('punchApproval:form.realStart')}</InputLabel>
                            <DateTimePicker
                                disabled={disableAttrbModel.realStartTime}
                                value={model.realStartTime || new Date()}
                                name="realStartTime"
                                onChange={handleChange}
                                onOpen={onOpenEvent}
                                onClose={onCloseEvent}
                                onBlur={onBlurEvent}
                                format="yyyy-MM-dd HH:mm"
                                popup={props => <Popup {...props} appendTo={_el} />}
                            />
                        </IntlProvider>
                        <FontAwesomeIcon onClick={assignScheduleStart} icon={faClock} className={disableAttrbModel.assignStartButton ? classes.faDisabled : classes.iconClock} />
                    </LocalizationProvider>
                    <FormControl className={`${classes.customRoot} ${styles.switchComponent}`} style={{ float: 'right', width:'20%' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={disableAttrbModel.isOvertimeBefore}
                                    checked={model.isOvertimeBefore}
                                    onChange={handleChange}
                                    name="isOvertimeBefore"
                                    variant="success"
                                />
                            }
                            label={i18next.t("punchApproval:form.isOvertimeBefore")}
                            labelPlacement="end"
                        />
                    </FormControl>
                </div>

                <div className={`${classes.divInput_80} ${styles.divDate}`} ref={el => (_el = el)}>
                    <LocalizationProvider language={locale}>
                        <IntlProvider locale={locale}>
                            <InputLabel className={showEndDateRequiredStar ? "required" : ""} htmlFor="realEndTime">{i18next.t('punchApproval:form.realEnd')}</InputLabel>
                            <DateTimePicker
                                disabled={disableAttrbModel.realEndTime}
                                value={model.realEndTime || new Date()}
                                name="realEndTime"
                                onOpen={handleOpen}
                                onChange={handleChange}
                                onOpen={onOpenEvent}
                                onClose={onCloseEvent}
                                onBlur={onBlurEvent}
                                format="yyyy-MM-dd HH:mm"
                                popup={props => <Popup {...props} appendTo={_el} />}
                            />
                        </IntlProvider>
                        <FontAwesomeIcon onClick={assignScheduleEnd} icon={faClock} className={disableAttrbModel.assignEndButton ? classes.faDisabled : classes.iconClock} />
                    </LocalizationProvider>
                    <FormControl className={`${classes.customRoot} ${styles.switchComponent}`} style={{ float: 'right', width: '20%', paddingBottom:'0px !important' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={disableAttrbModel.isOvertimeAfter}
                                    checked={model.isOvertimeAfter}
                                    onChange={handleChange}
                                    name="isOvertimeAfter"
                                    variant="success"
                                />
                            }
                            label={i18next.t("punchApproval:form.isOvertimeAfter")}
                            labelPlacement="end"
                        />
                    </FormControl>
                </div>

                <FormControl fullWidth>
                    <div className={`${classes.divInput_80} ${styles.divFormSelect}`}>
                        <InputLabel className={"required"} id="label-shift">{i18next.t('punchApproval:form.shift')}</InputLabel>
                        <Select
                            labelId="label-shift"
                            id="workRangeId"
                            name="workRangeId"
                            disabled={disableAttrbModel.workRangeId}
                            value={isNullOrEmpty(model.workRangeId) ? '' : parseInt(model.workRangeId)}
                            onChange={handleChange}
                            className={classes.select}
                            MenuProps={{ classes: { paper: styles.selectList } }}
                        >
                            {shifts.map((obj, index) =>
                                <MenuItem key={index} className={styles.selectItem} value={obj.shId}>{`${obj.shC} - ${IsEnglish() ? obj.shE : obj.shF}`}</MenuItem>
                            )}
                        </Select>
                        <FontAwesomeIcon onClick={assignSchedule} icon={faClock} className={disableAttrbModel.assignButton ? classes.faDisabled2 : classes.iconClock2} />
                    </div>
                </FormControl>

                <FormControl className={`${classes.customRoot} ${styles.switchComponent}`}>
                    <FormControlLabel
                        control={
                            <Switch
                                disabled={disableAttrbModel.isRecalculateTime}
                                checked={model.isRecalculateTime}
                                onChange={handleChange}
                                name="isRecalculateTime"
                                variant="success"
                            />
                        }
                        label={i18next.t("punchApproval:form.isRecalculateTime")}
                        labelPlacement="start"
                    />
                </FormControl>
                
                {dataPayCodesForm != null && dataPayCodesForm.map((obj, index) =>
                    <PayCodeControls key={index} dataItem={obj} disableAttrbModel={disableAttrbModel} items={dataPayCodesForm} handleChange={editItem} handleRemove={removeItem} handleFocus={handleFocus} payCodes={obj.index == 1 ? payCodesReg : payCodesOthers} />
                )}

                {dataPayCodesForm.length < maxPayCode &&
                    <div className={`${classes.divInput}`}>
                        <FontAwesomeIcon onClick={addItem} icon={faCirclePlus} className={disableAttrbModel.addPayCodeBtn ? classes.faDisabled : classes.iconAddPayCode} />
                    </div>
                }
                
                <FormControl fullWidth classes={{ root: classes.customRoot }}>
                    <TextField
                        id="comments"
                        name="comments"
                        label={i18next.t('punchApproval:form.commentSup')}
                        multiline
                        minRows={4}
                        className={styles.inputComments + " " + (showSupCommentRequiredStar ? "required" : "")}
                        onChange={handleChange}
                        disabled={disableAttrbModel.comments}
                        value={model.comments || ''}
                    />
                </FormControl>

                {!showAdditionalInfo &&
                    <div className={classes.divInput} style={{ textAlign: 'center' }} onClick={handleAdditionalInfoClick}>
                        <span style={{ fontSize: '12px', marginRight: '10px' }}>{i18next.t('punchApproval:button.additionInformation')}</span>
                        <FontAwesomeIcon icon={faChevronsDown} />
                    </div>
                }

                {showAdditionalInfo &&
                    <>
                        <FormControl fullWidth classes={{ root: classes.customRoot }}>
                            <TextField
                                id="payrollComment"
                                name="payrollComment"
                                label={i18next.t('punchApproval:form.commentPayroll')}
                                multiline
                                minRows={4}
                                className={styles.inputComments}
                                onChange={handleChange}
                                disabled={disableAttrbModel.payrollComment}
                                value={model.payrollComment || ''}
                            />
                        </FormControl>

                        {!isNullOrEmpty(model.employeComment) && <FormControl fullWidth classes={{ root: classes.customRoot }}>
                            <TextField
                                id="employeComment"
                                name="employeComment"
                                label={i18next.t('punchApproval:form.commentEmployee')}
                                multiline
                                minRows={4}
                                className={styles.inputComments}
                                onChange={handleChange}
                                disabled={disableAttrbModel.employeComment}
                                value={model.employeComment || ''}
                            />
                        </FormControl>}

                        {config.isUseWorkorderInPunch &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel className={"required"} id="label-workorder">{i18next.t('punchApproval:form.workorder')}</InputLabel>
                                    <Select
                                        labelId="label-workorder"
                                        id="workOrder"
                                        name="workOrder"
                                        disabled={disableAttrbModel.workOrder}
                                        value={model.workOrder || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {workorders.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.woCode}>{`${obj.woCode} - ${obj.woDesc}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {showElectronicDates &&
                            <>
                                <div className={styles.divDate} ref={el => (_el = el)}>
                                    <LocalizationProvider language={locale}>
                                        <IntlProvider locale={locale}>
                                            <InputLabel htmlFor="punchStart">{i18next.t('punchApproval:form.electronicStart')}</InputLabel>
                                            <DateTimePicker
                                                disabled={disableAttrbModel.punchStart}
                                                value={model.punchStart}
                                                name="punchStart"
                                                onChange={handleChange}
                                                format="yyyy-MM-dd HH:mm"
                                                popup={props => <Popup {...props} appendTo={_el} />}
                                            />
                                        </IntlProvider>
                                    </LocalizationProvider>
                                </div>

                                <div className={styles.divDate} ref={el => (_el = el)}>
                                    <LocalizationProvider language={locale}>
                                        <IntlProvider locale={locale}>
                                            <InputLabel htmlFor="punchEnd">{i18next.t('punchApproval:form.electronicEnd')}</InputLabel>
                                            <DateTimePicker
                                                disabled={disableAttrbModel.punchEnd}
                                                value={model.punchEnd}
                                                name="punchEnd"
                                                onChange={handleChange}
                                                format="yyyy-MM-dd HH:mm"
                                                popup={props => <Popup {...props} appendTo={_el} />}
                                            />
                                        </IntlProvider>
                                    </LocalizationProvider>
                                </div>
                            </>
                        }

                        <FormControl fullWidth>
                            <div className={styles.divFormSelect}>
                                <InputLabel className={"required"} id="label-department">{i18next.t('punchApproval:form.department')}</InputLabel>
                                <Select
                                    labelId="label-department"
                                    id="departmentCode"
                                    name="departmentCode"
                                    disabled={disableAttrbModel.departmentCode}
                                    value={model.departmentCode || ''}
                                    onChange={handleChange}
                                    className={classes.select}
                                    MenuProps={{ classes: { paper: styles.selectList } }}
                                >
                                    {departments.map((obj, index) =>
                                        <MenuItem key={index} className={styles.selectItem} value={obj.depC}>{`${obj.depC} - ${IsEnglish() ? obj.depE : obj.depF}`}</MenuItem>
                                    )}
                                </Select>
                            </div>
                        </FormControl>

                        <FormControl fullWidth>
                            <div className={styles.divFormSelect}>
                                <InputLabel id="label-jobTitle" className={showJobTitleRequiredStar ? "required" : ""}>{i18next.t('punchApproval:form.jobTitle')}</InputLabel>
                                <Select
                                    labelId="label-jobTitle"
                                    id="jobTitle"
                                    name="jobTitle"
                                    disabled={disableAttrbModel.jobTitle}
                                    value={model.jobTitle || ''}
                                    onChange={handleChange}
                                    className={classes.select}
                                    MenuProps={{ classes: { paper: styles.selectList } }}
                                >
                                    {jobTitles && jobTitles.map((obj, index) =>
                                        <MenuItem key={index} className={styles.selectItem} value={obj.jobC}>{`${obj.jobC} - ${IsEnglish() ? obj.jobE : obj.jobF}`}</MenuItem>
                                    )}
                                </Select>
                            </div>
                        </FormControl>
                            
                        {config.isUseAbc &&
                            <>
                                <FormControl fullWidth>
                                    <div className={styles.divFormSelect}>
                                        <InputLabel className={"required"} id="label-project">{i18next.t('punchApproval:form.project')}</InputLabel>
                                        <Select
                                            labelId="label-project"
                                            id="abcProjectCode"
                                            name="abcProjectCode"
                                            disabled={disableAttrbModel.abcProjectCode}
                                            value={model.abcProjectCode || ''}
                                            onChange={handleChange}
                                            className={classes.select}
                                            MenuProps={{ classes: { paper: styles.selectList } }}
                                        >
                                            {abcProjects.map((obj, index) =>
                                                <MenuItem key={index} className={styles.selectItem} value={obj.prC}>{`${obj.prC} - ${IsEnglish() ? obj.prE : obj.prF}`}</MenuItem>
                                            )}
                                        </Select>
                                    </div>
                                </FormControl>
                                <FormControl fullWidth>
                                    <div className={styles.divFormSelect}>
                                        <InputLabel className={"required"} id="label-activity">{i18next.t('punchApproval:form.activity')}</InputLabel>
                                        <Select
                                            labelId="label-activity"
                                            id="abcActivityCode"
                                            name="abcActivityCode"
                                            disabled={disableAttrbModel.abcActivityCode}
                                            value={isNullOrEmpty(model.abcActivityCode) ? '' : parseInt(model.abcActivityCode)}
                                            onChange={handleChange}
                                            className={classes.select}
                                            MenuProps={{ classes: { paper: styles.selectList } }}
                                        >
                                            {abcActivities.map((obj, index) =>
                                                <MenuItem key={index} className={styles.selectItem} value={obj.actC}>{`${obj.actC} - ${IsEnglish() ? obj.actE : obj.actF}`}</MenuItem>
                                            )}
                                        </Select>
                                    </div>
                                </FormControl>
                            </>
                        }

                        {config.isProjectEntry && !config.isPayProjectLoadedFromWorkorder &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel className={"required"} id="label-project-pay">{i18next.t('punchApproval:form.projectPay')}</InputLabel>
                                    <Select
                                        labelId="label-project-pay"
                                        id="payProjectCode"
                                        name="payProjectCode"
                                        disabled={disableAttrbModel.payProjectCode}
                                        value={model.payProjectCode || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {payProjects.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.prC}>{`${obj.prC} - ${IsEnglish() ? obj.prE : obj.prF}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {config.isActivityEntry && !config.isPayProjectLoadedFromWorkorder &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel className={"required"} id="label-activity-pay">{i18next.t('punchApproval:form.activityPay')}</InputLabel>
                                    <Select
                                        labelId="label-activity-pay"
                                        id="payActivityCode"
                                        name="payActivityCode"
                                        disabled={disableAttrbModel.payActivityCode}
                                        value={model.payActivityCode || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {payActivities.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.actC}>{`${obj.actC} - ${IsEnglish() ? obj.actE : obj.actF}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {config.isTaskEntry && !config.isPayProjectLoadedFromWorkorder &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel id="label-task" className={"required"}>{i18next.t('punchApproval:form.task')}</InputLabel>
                                    <Select
                                        labelId="label-task"
                                        id="taskCode"
                                        name="taskCode"
                                        disabled={disableAttrbModel.taskCode}
                                        value={model.taskCode || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {tasksPay && tasksPay.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.tkC}>{`${obj.tkC} - ${IsEnglish() ? obj.tkE : obj.tkF}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {config.isUseAbc && config.isUseActivityRemark &&
                            <FormControl fullWidth classes={{ root: classes.customRoot }}>
                                <TextField
                                    id="activityNote"
                                    name="activityNote"
                                    label={i18next.t('punchApproval:form.activityNote')}
                                    multiline
                                    minRows={4}
                                    className={`${styles.inputComments}`}                                    
                                    onChange={handleChange}
                                    disabled={disableAttrbModel.activityNote}
                                    value={model.activityNote || ''}
                                />
                            </FormControl>
                        }

                        {config.isBlockAssocWorkorderInPunch &&
                            <FormControl fullWidth classes={{ root: classes.customRoot }}>
                                <TextField
                                    id="bloc"
                                    name="bloc"
                                    label={i18next.t('punchApproval:form.bloc')}
                                    onChange={handleChange}
                                    disabled={disableAttrbModel.bloc}
                                    value={model.bloc || ''}
                                />
                            </FormControl>
                        }

                        {config.isUseOtherClassification &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel id="label-otherClassif" className={"required"}>{i18next.t('punchApproval:form.otherClassif')}</InputLabel>
                                    <Select
                                        labelId="label-otherClassif"
                                        id="otherClassification"
                                        name="otherClassification"
                                        disabled={disableAttrbModel.otherClassification}
                                        value={model.otherClassification || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {otherClassifs && otherClassifs.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.ocCode}>{`${obj.ocCode} - ${IsEnglish() ? obj.ocE : obj.ocF}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {config.isUseAbc && config.isUseRequisitionCodeOnPunch &&
                            <FormControl fullWidth>
                                <div className={styles.divFormSelect}>
                                    <InputLabel id="label-requisition" className={"required"}>{i18next.t('punchApproval:form.requisition')}</InputLabel>
                                    <Select
                                        labelId="label-requisition"
                                        id="requisitionCode"
                                        name="requisitionCode"
                                        disabled={disableAttrbModel.requisitionCode}
                                        value={model.requisitionCode || ''}
                                        onChange={handleChange}
                                        className={classes.select}
                                        MenuProps={{ classes: { paper: styles.selectList } }}
                                    >
                                        {requisitions && requisitions.map((obj, index) =>
                                            <MenuItem key={index} className={styles.selectItem} value={obj.reqCode}>{`${obj.reqCode} - ${obj.reqDesc}`}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </FormControl>
                        }

                        {config.isUseOrderFormReq &&
                            <FormControl fullWidth>
                                <TextField
                                    id="purchaseOrder"
                                    name="purchaseOrder"
                                    label={i18next.t('punchApproval:form.purchaseOrder')}
                                    onChange={handleChange}
                                    disabled={disableAttrbModel.purchaseOrder}
                                    value={model.purchaseOrder || ''}
                                />
                            </FormControl>
                        }

                        {config.usePersonnelRequisitionMileage &&
                            <>
                                <FormControl fullWidth>
                                    <DecimalTextField
                                        id="paidMileage"
                                        name="paidMileage"
                                        label={i18next.t('punchApproval:form.paidKM')}
                                        handleChange={handleChange}
                                        className={styles.inputComments}
                                        disabled={disableAttrbModel.paidMileage}
                                        value={model.paidMileage}
                                        onFocus={handleFocus}
                                        decimalScale={2}
                                    >
                                    </DecimalTextField>
                                </FormControl>

                                <FormControl fullWidth>
                                    <DecimalTextField
                                        id="chargedMileage"
                                        name="chargedMileage"
                                        label={i18next.t('punchApproval:form.chargedKM')}
                                        handleChange={handleChange}
                                        className={styles.inputComments}
                                        value={model.chargedMileage}
                                        disabled={disableAttrbModel.chargedMileage}
                                        onFocus={handleFocus}
                                        decimalScale={2}
                                    >
                                    </DecimalTextField>
                                </FormControl>
                            </>
                        }
                    </>
                }

                {showAdditionalInfo && model.punchId > 0 &&
                    <div className={classes.divInput} style={{ textAlign: 'center' }} onClick={handleHideAdditionalInfo}>
                        <span style={{ fontSize: '12px', marginRight: '10px' }}>{i18next.t('punchApproval:button.hideAdditionInformation')}</span>
                        <FontAwesomeIcon icon={faChevronsUp} className={classes.iconHideAdditionalInfo} />
                    </div>
                }

                <FormControl className={styles.divButtons}>
                    <Button
                        className={styles.saveButton}
                        disabled={disableAttrbModel.saveButton}
                        onClick={handleSaveClick}
                        variant="success"
                    >
                        {i18next.t('buttons:save')}
                    </Button>

                    <Button
                        className={styles.resetButton}
                        disabled={disableAttrbModel.applyButton}
                        onClick={handleApplyClick}                        
                        variant="success"
                    >
                        {i18next.t('buttons:apply')}
                    </Button>

                    {model.punchId > 0 && isDeleteBtnVisible &&
                        <Button
                            className={`${styles.deleteButton}`}
                            disabled={disableAttrbModel.deleteButton}
                            onClick={handleOpenConfirmDelete}
                            variant="danger"
                        >
                            {i18next.t('buttons:delete')}
                        </Button>
                    }
                </FormControl>
            </div>
            <Dialog
                open={openConfirm}
                onClose={handleCloseConfirm}
                aria-labelledby="draggable-dialog-title"
                style={{ zIndex: 10001 }}
            >
                <DialogTitle id="draggable-dialog-title">
                    {i18next.t('punchApproval:dialog.attention')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {messageConfirm}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleDeleteClick} color="primary" className={styles.defaultButton} variant="contained">
                        {i18next.t('buttons:yes')}
                    </Button>
                    <Button onClick={handleCloseConfirm} color="primary" className={styles.defaultButton} variant="contained">
                        {i18next.t('buttons:no')}
                    </Button>
                </DialogActions>
            </Dialog>
            <AlertAlgo ref={alertRef} />
        </LoadingLayer>
    )
}

export default withNamespaces(['punchApproval', 'button', 'error'], { wait: true })(PunchForm);